import type { ReactNode } from "react"
import InfoTooltip from "~/components/reusable/InfoTooltip"
import { cn } from "~/utils/helpers"

/**
 * Label-above-value stat for insight panels: `base` for grid-packed views, `lg` for single-entity
 * panels. `dot` renders a chart-series legend dot; pass the series' *text* colour class, since the
 * dot is `bg-current`. `suffix` sits inline after the value, e.g. a coloured change like `+37%`.
 * Approved surface: design-system/APPROVED.md.
 */
export const Kpi = ({
  label,
  value,
  info,
  size = "base",
  dot,
  suffix,
  className,
}: {
  label: string
  value: string
  info?: string
  size?: "base" | "lg"
  /** Text-colour class for a chart-series legend dot, e.g. `text-fg-interactive`. */
  dot?: string
  suffix?: ReactNode
  className?: string
}) => (
  <div className={cn("flex flex-col", size === "lg" ? "gap-1" : "gap-0.5", className)}>
    {/* items-start + a first-line offset keeps the dot beside the first line when a label wraps. */}
    <span className="paragraph-sm text-fg-subtle flex items-start gap-1">
      {dot && (
        <span
          className={cn(
            "mt-[calc((1lh-0.375rem)/2)] size-1.5 shrink-0 rounded-full bg-current",
            dot,
          )}
        />
      )}
      {label}
      {info && <InfoTooltip title={info} className="size-3 self-center" />}
    </span>
    <span className="flex items-baseline gap-1.5">
      <span className={cn("tabular-nums", size === "lg" ? "label-lg" : "label-base")}>{value}</span>
      {suffix}
    </span>
  </div>
)
